import myAxios from "@/services/api";
import { API_GW_URL } from "@/config/api";
import { API_GW_URL_SSE } from "@/config/api";

const NOTIFICATION_API_URL = `${API_GW_URL}/notification/`;
const FETCH_URL = NOTIFICATION_API_URL + "getNotificationsAll";
const CONSUME_PROXY_URL = API_GW_URL_SSE + "/notification/consume_sse_proxy";
const MARK_AS_READ_URL = NOTIFICATION_API_URL + "updateNotificationStatus";

const maskToken = (token = "") => {
  if (!token || token.length <= 12) {
    return "***";
  }
  return `${token.slice(0, 6)}...${token.slice(-6)}`;
};

const getSafeSSEUrl = (urlString) => {
  try {
    const safeUrl = new URL(urlString);
    const userToken = safeUrl.searchParams.get("user_token");
    if (userToken) {
      safeUrl.searchParams.set("user_token", maskToken(userToken));
    }
    return safeUrl.toString();
  } catch {
    return "[invalid_sse_url]";
  }
};

let eventSource = null;
let notificationTimeout = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 3;
const SSE_OPEN_TIMEOUT_MS = 15000;
let sseInitializing = false;

export default {
  clearNotifications(context) {
    context.commit("clearNotifications");
  },

  async refreshNotifications(context) {
    const loggedUser = context.rootGetters.getUserDetailList;
    if (!loggedUser || !loggedUser.usr_username) {
      context.dispatch("clearNotifications");
      return;
    }

    context.dispatch("clearNotifications");
    await new Promise((resolve) => setTimeout(resolve, 100));
    await context.dispatch("fetchNotifications");
  },

  async fetchNotifications(context) {
    const loggedUser = context.rootGetters.getUserDetailList;
    const userToken = context.rootGetters.getUserSession?.app_token;

    if (!loggedUser || !loggedUser.usr_username) {
      return;
    }

    try {
      const response = await myAxios.post(FETCH_URL, {
        token: userToken,
        username: loggedUser.usr_username,
      });

      if (response.data?.state !== "ok") {
        const errorMessage =
          response.data?.message ||
          "Falha no processo de obtenção das notificações.";
        throw new Error(errorMessage);
      }

      const mappedNotifications = [];

      if (response.data.data.data && Array.isArray(response.data.data.data)) {
        response.data.data.data.forEach((notification) => {
          mappedNotifications.push({
            id: notification.not_app_id,
            sourceGroup: notification.origin,
            sourceApp: notification.teamScope,
            date: notification.created_at.slice(0, 19),
            msg: notification.msg,
            read: notification.stateID === 1 || notification.stateID === true,
            hashMD5: notification.hashMD5,
          });
        });
      }

      context.commit("setNotifications", mappedNotifications);
      context.commit(
        "setUnreadCount",
        mappedNotifications.filter((n) => !n.read).length
      );
    } catch (error) {
      let normalizedMessage = "Falha no processo de obtenção das notificações.";
      if (error && error.data) {
        if (typeof error.data === "string" && error.data.trim()) {
          normalizedMessage = error.data.trim();
        } else if (error.data.message) {
          normalizedMessage = error.data.message;
        }
      } else if (error && error.message) {
        normalizedMessage = error.message;
      } else if (!error) {
        normalizedMessage = "Servidor não respondeu ao pedido de notificações.";
      }

      throw new Error(normalizedMessage);
    }
  },

  async closeSSE() {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
      notificationTimeout = null;
    }
    reconnectAttempts = 0;
    sseInitializing = false;
  },

  async initSSE({ commit, rootGetters, dispatch }) {
    console.info("[SSE] init requested");
    if (sseInitializing || eventSource) {
      return;
    }

    sseInitializing = true;

    const showNotificationModal = (notification) => {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
        commit("toggleNotificationModal", false);
      }
      commit("setCurrentNotification", notification);
      commit("toggleNotificationModal", true);
      notificationTimeout = setTimeout(() => {
        commit("toggleNotificationModal", false);
      }, 8000);
    };

    const scheduleReconnect = () => {
      sseInitializing = false;
      console.warn("[SSE] reconnect scheduled", {
        attempt: reconnectAttempts + 1,
        maxAttempts: MAX_RECONNECT_ATTEMPTS,
      });
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts++;
        setTimeout(
          () => dispatch("initSSE", { commit, rootGetters, dispatch }),
          5000
        );
      } else {
        setTimeout(() => {
          reconnectAttempts = 0;
          dispatch("initSSE", { commit, rootGetters, dispatch });
        }, 120000);
      }
    };

    // Fechar conexão anterior se existir
    await dispatch("closeSSE");

    const loggedUser = rootGetters.getUserDetailList;
    const username = loggedUser?.usr_username;
    const userToken = rootGetters.getUserSession?.app_token;

    if (!username || !userToken) {
      sseInitializing = false;
      return;
    }

    try {
      try {
        await dispatch("fetchNotifications");
      } catch (error) {
        console.warn("[SSE] initial notification fetch failed", {
          message: error?.message,
        });
      }

      const urlWithParams = new URL(CONSUME_PROXY_URL);
      urlWithParams.searchParams.append("user_token", userToken);
      urlWithParams.searchParams.append("username", username);

      const safeSSEUrl = getSafeSSEUrl(urlWithParams.toString());
      console.info("[SSE] opening stream", { url: safeSSEUrl, username });

      const es = new EventSource(urlWithParams.toString());
      let reconnectScheduled = false;

      const forceReconnect = (reason, extra = {}) => {
        if (reconnectScheduled) {
          return;
        }
        reconnectScheduled = true;
        console.error("[SSE] forcing reconnect", {
          reason,
          url: safeSSEUrl,
          ...extra,
        });
        es.close();
        eventSource = null;
        scheduleReconnect();
      };

      const openTimeoutId = setTimeout(() => {
        if (es.readyState === EventSource.CONNECTING) {
          forceReconnect("open-timeout", {
            timeoutMs: SSE_OPEN_TIMEOUT_MS,
            readyState: es.readyState,
          });
        }
      }, SSE_OPEN_TIMEOUT_MS);

      es.onopen = function () {
        clearTimeout(openTimeoutId);
        reconnectAttempts = 0;
        sseInitializing = false;
        console.info("[SSE] stream opened", {
          readyState: es.readyState,
          url: safeSSEUrl,
        });
      };

      es.onmessage = async function (event) {
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.type === "heartbeat") {
            return;
          }

          if (parsedData.error) {
            forceReconnect("payload-error", {
              payloadError: parsedData.error,
            });
            return;
          }
          const notification = parsedData.payload;
          if (notification.not_app_id) {
            if (
              notification.targetScope &&
              notification.targetScope !== username
            ) {
              return;
            }

            const mappedNotification = {
              id: notification.not_app_id,
              sourceGroup: notification.origin,
              sourceApp: notification.teamScope,
              date: new Date(notification.created_at)
                .toISOString()
                .slice(0, 19)
                .replace("T", " "),
              msg: notification.msg,
              read: notification.stateID === 1 || notification.stateID === true,
              hashMD5: notification.hashMD5 || "-",
              targetScope: notification.targetScope,
              geolocationScope: notification.geolocationScope,
              pushNotification: notification.pushNotification,
              isApp: notification.isApp,
            };

            if (
              mappedNotification.msg &&
              mappedNotification.sourceGroup &&
              !mappedNotification.read
            ) {
              commit("addNotification", mappedNotification);
              showNotificationModal(mappedNotification);

              try {
                await dispatch("fetchNotifications");
              } catch (error) {
                console.warn("[SSE] notification refresh failed", {
                  message: error?.message,
                });
              }
            }
          }
        } catch (parseError) {
          console.warn("[SSE] failed to parse event payload", {
            message: parseError?.message,
          });
          return;
        }
      };

      es.onerror = function (errorEvent) {
        clearTimeout(openTimeoutId);
        forceReconnect("onerror", {
          readyState: es.readyState,
          reconnectAttempts,
          eventType: errorEvent?.type,
        });
      };

      eventSource = {
        close: () => {
          es.close();
        },
      };
    } catch (error) {
      console.error("[SSE] init failed", {
        message: error?.message,
      });
      scheduleReconnect();
      return;
    }
  },

  async markAsRead(context, notification) {
    const loggedUser = context.rootGetters.getUserDetailList;
    const userToken = context.rootGetters.getUserSession?.app_token;

    if (notification.read) {
      return;
    }

    const response = await myAxios.post(MARK_AS_READ_URL, {
      token: userToken,
      username: loggedUser.usr_username,
      not_app_id: notification.id,
      hashMD5: notification.hashMD5 || "-",
    });

    if (response.data?.state !== "ok") {
      const errorMessage =
        response.data?.message ||
        "Falha no processo de atualização das notificações.";
      throw new Error(errorMessage);
    } else {
      context.commit("markNotificationAsRead", notification);
    }
  },
};

